import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import Card from '../Base/Card';
import SkeletonText from '../Skeleton/SkeletonText';
import AdaptiveTrait from '../../AdaptiveTrait';
import { 
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export default class MortgageCompareBlock extends React.Component {

    constructor(props) {

        super(props);

        this.state = {

            textBlock: {
                title: null,
                subtitle: null,
                paymentBenefit: null,
                paymentBadge: null,
                firstBenefit: null,
                firstBenefitValue: null,
                secondBenefit: null,
                secondBenefitValue: null,
            },

            chartBlock: {
                title: null,
                subtitle: null,
                mortgageLabel: null,
                mortgageValue: null,
                rentLabel: null,
                rentValue: null,
                taxLabel: null,
                taxValue: null,
                diffLabel: null,
                diffValue: null,
            },
            
        };

        this.components = {
            chart: null,
        };
    }

    static get propTypes() {

        return {
            hasTax: PropTypes.bool,
        };
    }

    getChartOptions() {

        return {
            cutout: 100,
            responsive: true,
            borderSkipped: true,
            scales: {
                x: {
                    stacked: true,
                    grid: { display: false },
                    ticks: {
                        display: false,
                    },
                },
                y: {
                    display: false,
                    grid: { display: false },
                },
            },
            animation: false,
            plugins: {
                legend: false,
                colors: false,
                tooltip: {
                    backgroundColor: '#fff',
                    padding: 20,
                    titleColor: '#000',
                    bodyColor: '#000',
                    footerColor: '#000',
                    titleFont: {
                        family: '"Montserrat", sans-serif',
                        size: 16,
                        lineHeight: 1.5,
                    },
                    bodyFont: {
                        family: '"Montserrat", sans-serif',
                        size: 16,
                        lineHeight: 1.5,
                    },
                    footerFont: {
                        family: '"Montserrat", sans-serif',
                        size: 16,
                        lineHeight: 1.5,
                    },
                },
            },
        };
    }

    hasChartData() {

        return !!this.state.chartBlock?.mortgageValue && !!this.state.chartBlock?.rentValue;
    }

    getChartData() {


        return {
            labels: [
                this.state.chartBlock.mortgageLabel,
                this.state.chartBlock.taxLabel,
                this.state.chartBlock.rentLabel,
            ].filter((value) => value != null),
            datasets: [
                {
                    data: [
                        this.state.chartBlock?.mortgageValue?.round().toInt(),
                        this.state.chartBlock?.taxValue?.round().toInt(),
                        this.state.chartBlock?.rentValue?.round().toInt(),
                    ].filter((value) => value != null),
                    backgroundColor: this.props.hasTax
                        ? ['#e3405e', '#fabd48', '#9556d3']
                        : ['#1373E5', '#10B97B'],
                },
            ],
        };
    }

    componentDidUpdate() {

        if (this.components.chart) {

            this.components.chart.data = this.getChartData();
            this.components.chart.update('none');
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    render() {

        return (
            <div className={ style('w-full flex gap mobile:flex-wrap') }>
                { this.renderTextBlock() }
                { this.renderChartBlock() }
            </div>
        );
    }

    renderTextBlock() {

        const
            badgeClasses = ['badge badge-sm px-4 rounded-full mobile:w-full text-center'],
            badgeColor = this.state.textBlock.paymentBenefit?.toFloat() > 0
                ? 'badge-success-opacity'
                : 'badge-danger-opacity';

            badgeClasses.push(badgeColor);

        return (
            <Card className={ style('flex-1') }>

                {/* titles */}
                { this.renderTitles(this.state.textBlock.title, this.state.textBlock.subtitle) }

                {/* payment */}
                <div className={ style('my-6 flex mobile:flex-wrap items-center gap-md') }>
                    <SkeletonText className={ style('title1 mobile:w-full') } size={ 160 }>
                        { this.state.textBlock.paymentBenefit?.round().formatted() }
                    </SkeletonText>

                    <SkeletonText size={ 128 } when={ () => !!this.state.textBlock.paymentBadge }>
                        <div className={ style(badgeClasses.join(' ')) }>
                            { this.state.textBlock.paymentBadge }
                        </div>
                    </SkeletonText>
                </div>

                {/* details */}
                <div className={ style('flex flex-wrap w-full gap') }>
                    {
                        this.renderTextBlockDetail(
                            this.state.textBlock.firstBenefit,
                            this.state.textBlock.firstBenefitValue
                        )
                    }
                    {
                        this.renderTextBlockDetail(
                            this.state.textBlock.secondBenefit,
                            this.state.textBlock.secondBenefitValue
                        )
                    }
                </div>

            </Card>
        );
    }

    renderChartBlock() {

        return (
            <Card className={ style('w-60 mobile:w-full') }>

                { this.renderTitles(this.state.chartBlock.title, this.state.chartBlock.subtitle) }

                <div className={ style('flex flex-wrap w-full mt-10 gap') }>

                    <SkeletonText className={ style('w-6/12 mobile:w-full') }
                        skeletonClassName={ style('aspect-video') } when={ () => this.hasChartData() }>
                        
                        <Bar
                            ref={ (ref) => this.components.chart = ref }
                            style={{ position: 'relative', zIndex: 1000 }}
                            options={ this.getChartOptions() }
                            data={ this.getChartData() } />
                            
                    </SkeletonText>

                    <div className={ style('flex flex-wrap flex-1 gap') }>
                        {
                            this.renderTextBlockDetail(
                                this.state.chartBlock.mortgageLabel,
                                this.state.chartBlock.mortgageValue,
                                /** bold */ false,
                                /** with border */ false,
                            )
                        }
                        {
                            this.renderTextBlockDetail(
                                this.state.chartBlock?.taxLabel,
                                this.state.chartBlock?.taxValue,
                                /** bold */ false,
                                /** with border */ true,
                                /** when */ () => this.props.hasTax,
                            )
                        }
                        {
                            this.renderTextBlockDetail(
                                this.state.chartBlock.rentLabel,
                                this.state.chartBlock.rentValue,
                                /** bold */ false,
                            )
                        }
                        {
                            this.renderTextBlockDetail(
                                this.state.chartBlock.diffLabel,
                                this.state.chartBlock.diffValue,
                            )
                        }
                    </div>

                </div>

            </Card>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER CHUNKS
    //------------------------------------------------------------------------------------------------------------------

    renderTextBlockDetail(label, value, isBold = true, withBorder = true, when = null) {

        if (when != null && !when())
            return null;

        const classes = ['flex w-full justify-between'];
        if (isBold)
            classes.push('text-bold');

        return (<>

            {
                withBorder
                    ? <div className={ style('border-top w-full') } />
                    : null
            }

            <div className={ style(classes.join(' ')) }>
                <SkeletonText size='40%'>{ label }</SkeletonText>
                <SkeletonText size='40%'>{ value?.round().formatted() }</SkeletonText>
            </div>

        </>);
    }

    renderTitles(title, subtitle) {

        return (<>
            <SkeletonText className={ style('title3') }>{ title }</SkeletonText>
            <SkeletonText className={ style('text-muted mt-2') } rows={ AdaptiveTrait.isMobile() ? 2 : 1 }>
                { subtitle }
            </SkeletonText>
        </>);
    }

}